import type { StudioRuntimeConfig } from "../../../env.js";
import {
  isStudioEligibilityBlockedError,
  type StudioApiClient,
  type StudioEligibilityBlockedBody,
} from "../../../api.js";
import type { PublicScanStatusV1, StudioIntakeSummaryV1 } from "@enrein/shared-contracts";
import type { StudioProjectModeV1 } from "@enrein/shared-contracts";
import { clientLinkUrl } from "../../../client-link-url.js";
import { saveClientLinkSession } from "../../../client-link-session-store.js";
import type { BusinessGraphV1 } from "@enrein/shared-contracts";
import {
  buildRichnessEligibilityBlockedResponse,
  evaluateBusinessGraphRichness,
  evaluateBusinessProfileReadiness,
  isGenerationPackageEligible,
  recipeIdFromSitePackageId,
  resolvePackPinFromGraph,
  resolveStrategyIdForGraph,
  SitePackageIdSchema,
  type SitePackageId,
} from "@enrein/shared-contracts";

export interface StudioNewProjectFormInput {
  readonly businessName: string;
  readonly primaryService: string;
  readonly serviceArea: string;
  readonly contactEmail: string;
  readonly primaryPhone?: string;
  readonly services?: ReadonlyArray<{ readonly name: string }>;
  readonly serviceAreas?: ReadonlyArray<{ readonly name: string }>;
  readonly description?: string;
  readonly primaryEvidenceType?: string;
  readonly sitePackageId?: string;
  readonly lensId?: string;
  /** AG-002 delivery inputs */
  readonly clientEntityName: string;
  readonly scopeSummary: string;
  readonly ownerLabel: string;
  readonly builderPath: "ENREIN_GENERATE" | "EXTERNAL_SUPPORTED";
  readonly targetOutcome: string;
}

export interface StudioAgencyEnterResult {
  readonly projectId: string;
  readonly businessName: string;
}

export interface StudioIntakeLinkResult {
  readonly projectId: string;
  readonly businessName: string;
  readonly intakeUrl: string;
}

export interface StudioGenerateResult {
  readonly runId: string;
  readonly status: string;
}

export function slugProjectId(businessName: string): string {
  const base = businessName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 32);
  const suffix = Date.now().toString(36);
  return `project_${base || "new"}_${suffix}`;
}

export function buildGenerateIdempotencyKey(projectId: string, revisionId: string): string {
  return `studio-generate:${projectId}:${revisionId}`;
}

const GENERATION_PACKAGE_PREFERENCE: readonly SitePackageId[] = [
  "package-authority-15",
  "package-authority-10",
  "package-growth-8",
  "package-standard-5",
  "package-basic-3",
  "package-one-page",
];

export function resolveGenerationSitePackageId(
  businessGraph: BusinessGraphV1,
  requested?: string,
): SitePackageId {
  const requestedParse = requested ? SitePackageIdSchema.safeParse(requested) : null;
  const preferred = SitePackageIdSchema.parse(
    requestedParse?.success
      ? requestedParse.data
      : (businessGraph.layout?.sitePackageId ?? "package-standard-5"),
  );

  if (isGenerationPackageEligible(businessGraph, preferred)) {
    return preferred;
  }

  const readiness = evaluateBusinessProfileReadiness(businessGraph);
  const candidates = [
    ...GENERATION_PACKAGE_PREFERENCE.filter((pkg) => readiness.availablePackages.includes(pkg)),
    ...readiness.availablePackages,
  ];
  for (const pkg of [...new Set(candidates)]) {
    if (isGenerationPackageEligible(businessGraph, pkg)) {
      return pkg;
    }
  }

  const blockers =
    readiness.hardBlockers.length > 0
      ? readiness.hardBlockers.join("; ")
      : readiness.missingRequirements.join("; ") || "profile not ready for any site package";
  throw new Error(`Business profile cannot generate a site yet: ${blockers}`);
}

export type PackageEligibilityBlock = ReturnType<typeof buildRichnessEligibilityBlockedResponse>;

export type PackageEligibilityBlocked = {
  readonly requestedPackage: SitePackageId;
  readonly suggestedFallbackPackage: SitePackageId | null;
  readonly block: PackageEligibilityBlock;
};

function suggestFallbackPackage(
  businessGraph: BusinessGraphV1,
  requestedPackage: SitePackageId,
): SitePackageId | null {
  const startIdx = GENERATION_PACKAGE_PREFERENCE.indexOf(requestedPackage);
  const searchOrder =
    startIdx >= 0
      ? GENERATION_PACKAGE_PREFERENCE.slice(startIdx + 1)
      : [...GENERATION_PACKAGE_PREFERENCE];
  for (const pkg of searchOrder) {
    if (isGenerationPackageEligible(businessGraph, pkg)) {
      return pkg;
    }
  }
  return null;
}

export function checkPackageEligibility(
  businessGraph: BusinessGraphV1,
  requestedPackage: SitePackageId,
): { readonly eligible: true } | { readonly eligible: false; readonly blocked: PackageEligibilityBlocked } {
  if (isGenerationPackageEligible(businessGraph, requestedPackage)) {
    return { eligible: true };
  }
  const readiness = evaluateBusinessProfileReadiness(businessGraph);
  const richness = evaluateBusinessGraphRichness(businessGraph);
  return {
    eligible: false,
    blocked: {
      requestedPackage,
      suggestedFallbackPackage: suggestFallbackPackage(businessGraph, requestedPackage),
      block: buildRichnessEligibilityBlockedResponse(
        readiness,
        richness,
        requestedPackage,
        undefined,
        businessGraph,
      ),
    },
  };
}

function asSitePackageIdList(values: readonly string[] | undefined): SitePackageId[] {
  if (!values) return [];
  const out: SitePackageId[] = [];
  for (const value of values) {
    const parsed = SitePackageIdSchema.safeParse(value);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

/**
 * Convert a structured activate/create-run ELIGIBILITY_BLOCKED body into the
 * existing PackageEligibilityBlocked workflow shape (card + toast).
 * Do not invent a fallback package — availablePackages is readiness-tier only
 * and is not generation-eligibility proof without the business graph.
 */
export function packageEligibilityBlockedFromApiBody(
  body: StudioEligibilityBlockedBody,
): PackageEligibilityBlocked {
  const requestedPackage = SitePackageIdSchema.parse(body.requestedPackage);
  const availablePackages = asSitePackageIdList(body.availablePackages);

  const block = {
    status: "ELIGIBILITY_BLOCKED" as const,
    requestedPackage,
    currentReadinessLevel: (body.currentReadinessLevel ?? "NEEDS_BASICS") as PackageEligibilityBlock["currentReadinessLevel"],
    availablePackages,
    blockedPackages: asSitePackageIdList(body.blockedPackages),
    missingRequirements: [...(body.missingRequirements ?? [])],
    hardBlockers: [...(body.hardBlockers ?? [])],
    warnings: [...(body.warnings ?? [])],
    policyVersion: body.policyVersion ?? "unknown",
    richnessScore: typeof body.richnessScore === "number" ? body.richnessScore : 0,
    minimumRichnessRequired:
      typeof body.minimumRichnessRequired === "number" ? body.minimumRichnessRequired : 0,
    richnessBlockReason: body.richnessBlockReason,
    packageRequiredFieldBlockReason: body.packageRequiredFieldBlockReason,
  } satisfies PackageEligibilityBlock;

  return {
    requestedPackage,
    suggestedFallbackPackage: null,
    block,
  };
}

function sanitizeWorkflowErrorMessage(message: string): string {
  return message.replace(/^studio_api_error_(\d+)/, "Request failed ($1)");
}

const API_UNREACHABLE_HINT =
  "API not reachable — start Docker Desktop, then run: pnpm db:up && pnpm dev:api";

export function formatWorkflowError(error: unknown): string {
  if (error instanceof Error) {
    if (error.message === "Failed to fetch") {
      return API_UNREACHABLE_HINT;
    }
    return sanitizeWorkflowErrorMessage(error.message);
  }
  if (typeof error === "string" && error.trim().length > 0) {
    if (error === "Failed to fetch") {
      return API_UNREACHABLE_HINT;
    }
    return sanitizeWorkflowErrorMessage(error);
  }
  return "Something went wrong.";
}

function draftPayload(input: StudioNewProjectFormInput) {
  const services =
    input.services && input.services.length > 0
      ? input.services
      : input.primaryService.trim()
        ? [{ name: input.primaryService.trim() }]
        : [];
  const serviceAreas =
    input.serviceAreas && input.serviceAreas.length > 0
      ? input.serviceAreas
      : input.serviceArea.trim()
        ? [{ name: input.serviceArea.trim() }]
        : [];
  return {
    businessName: input.businessName.trim(),
    primaryEmail: input.contactEmail.trim() || undefined,
    primaryPhone: input.primaryPhone?.trim() || undefined,
    description: input.description?.trim() || undefined,
    primaryEvidenceType: input.primaryEvidenceType,
    services,
    serviceAreas,
    sitePackageId: input.sitePackageId,
    lensId: input.lensId,
  };
}

async function ensureProject(
  api: StudioApiClient,
  projectId: string,
  input: StudioNewProjectFormInput,
  projectMode?: StudioProjectModeV1,
): Promise<void> {
  const businessName = input.businessName.trim();
  const builderPath =
    input.builderPath ??
    (projectMode === "EXTERNAL_PROOF_ONLY" || projectMode === "EXISTING_SITE_SCAN"
      ? "EXTERNAL_SUPPORTED"
      : "ENREIN_GENERATE");
  try {
    await api.createProject({
      projectId,
      businessName,
      projectMode,
      clientEntityName: input.clientEntityName.trim() || businessName,
      scopeSummary: input.scopeSummary.trim(),
      ownerLabel: input.ownerLabel.trim(),
      builderPath,
      sitePackageId: input.sitePackageId ?? "package-standard-5",
      targetOutcome: input.targetOutcome.trim(),
    });
  } catch (error) {
    if (!(error instanceof Error) || !error.message.includes("409")) {
      throw error;
    }
  }
}

export async function pollPublicScanUntilComplete(
  api: StudioApiClient,
  scanId: string,
  options?: { readonly timeoutMs?: number; readonly intervalMs?: number },
): Promise<PublicScanStatusV1> {
  const timeoutMs = options?.timeoutMs ?? 120_000;
  const intervalMs = options?.intervalMs ?? 2_500;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const status = await api.getPublicScanStatus(scanId);
    if (status.status === "COMPLETE" || status.status === "FAILED") {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error("Scan timed out — try again in a moment.");
}

export async function agencyEnterProject(
  api: StudioApiClient,
  input: StudioNewProjectFormInput,
  projectMode: StudioProjectModeV1 = "DEFAULT_GENERATION",
): Promise<StudioAgencyEnterResult> {
  const businessName = input.businessName.trim();
  const projectId = slugProjectId(businessName);

  await ensureProject(api, projectId, input, projectMode);

  const draft = await api.createBusinessGraphDraft(projectId, draftPayload(input));
  const validation = await api.validateBusinessGraphDraft(projectId, draft.draftId);
  if (!validation.valid) {
    const issues = validation.issues ?? [];
    const detail = issues.map((i) => i.message).join("; ") || "Validation failed";
    throw new Error(detail);
  }
  await api.activateBusinessGraphDraft(projectId, draft.draftId);

  return { projectId, businessName };
}

export async function createIntakeLinkProject(
  api: StudioApiClient,
  config: StudioRuntimeConfig,
  input: StudioNewProjectFormInput,
  projectMode: StudioProjectModeV1 = "DEFAULT_GENERATION",
): Promise<StudioIntakeLinkResult> {
  const businessName = input.businessName.trim();
  const projectId = slugProjectId(businessName);

  await ensureProject(api, projectId, input, projectMode);

  const link = await api.createClientLink(projectId, { clientDisplayName: businessName });
  saveClientLinkSession(projectId, link);
  const intakeUrl = clientLinkUrl(config, link.shellPath);

  return { projectId, businessName, intakeUrl };
}

export interface StudioRelaunchScanResult {
  readonly projectId: string;
  readonly businessName: string;
  readonly publicReportRef: string;
  readonly intakeUrl: string;
}

export async function createRelaunchScanProject(
  api: StudioApiClient,
  config: StudioRuntimeConfig,
  input: StudioNewProjectFormInput,
  scanUrl: string,
): Promise<StudioRelaunchScanResult> {
  const trimmedUrl = scanUrl.trim();
  if (!trimmedUrl) throw new Error("Enter a website URL to scan.");

  const created = await api.createPublicScan(trimmedUrl);
  const status = await pollPublicScanUntilComplete(api, created.scanId);
  if (status.status === "FAILED" || !status.publicReportRef) {
    throw new Error("Scan failed — check the URL and try again.");
  }
  await api.claimPublicReport(status.publicReportRef);

  const businessName = input.businessName.trim() || "Relaunch client";
  const projectId = slugProjectId(businessName);
  await ensureProject(api, projectId, { ...input, businessName }, "EXISTING_SITE_SCAN");
  await api.attachExternalSite(projectId, trimmedUrl);

  const link = await api.createClientLink(projectId, { clientDisplayName: businessName });
  saveClientLinkSession(projectId, link);
  const intakeUrl = clientLinkUrl(config, link.shellPath);

  return { projectId, businessName, publicReportRef: status.publicReportRef, intakeUrl };
}

export interface StudioExternalProofResult {
  readonly projectId: string;
  readonly businessName: string;
  readonly intakeUrl: string;
}

export async function createExternalProofProject(
  api: StudioApiClient,
  config: StudioRuntimeConfig,
  input: StudioNewProjectFormInput,
  externalUrl: string,
): Promise<StudioExternalProofResult> {
  const trimmedUrl = externalUrl.trim();
  if (!trimmedUrl) throw new Error("Enter an external site URL.");

  const businessName = input.businessName.trim() || "External proof client";
  const projectId = slugProjectId(businessName);
  await ensureProject(api, projectId, { ...input, businessName }, "EXTERNAL_PROOF_ONLY");
  await api.attachExternalSite(projectId, trimmedUrl);
  await api.generateExternalReadiness(projectId);
  await api.generateExternalProofFixKit(projectId);
  const link = await api.createClientLink(projectId, { clientDisplayName: businessName });
  await api.generateExternalProofSalesRoom(projectId, { clientLinkId: link.clientLinkId });
  saveClientLinkSession(projectId, link);
  const intakeUrl = clientLinkUrl(config, link.shellPath);

  return { projectId, businessName, intakeUrl };
}

export async function validateAndActivateDraft(api: StudioApiClient, projectId: string): Promise<void> {
  const status = await api.getBusinessGraphStatus(projectId);
  const draftId = status.latestDraftId;
  if (!draftId) {
    throw new Error("No Business Profile draft found for this project.");
  }
  const validation = await api.validateBusinessGraphDraft(projectId, draftId);
  if (!validation.valid) {
    const detail = (validation.issues ?? []).map((i) => i.message).join("; ") || "Validation failed";
    throw new Error(detail);
  }
  await api.activateBusinessGraphDraft(projectId, draftId);
}

export async function generateSite(
  api: StudioApiClient,
  projectId: string,
  options?: {
    readonly sitePackageId?: string;
    /** Agency preference before accepting a smaller built package (Phase 3). */
    readonly agencyRequestedSitePackageId?: string;
    readonly lensId?: string;
    readonly allowAutoDowngrade?: boolean;
  },
): Promise<StudioGenerateResult> {
  const [active, status] = await Promise.all([
    api.getActiveBusinessGraph(projectId),
    api.getBusinessGraphStatus(projectId),
  ]);
  const revisionId = status.currentBusinessGraphRevisionId;
  if (!revisionId) {
    throw new Error("Activate a Business Profile revision before generating.");
  }

  const requestedRaw =
    options?.sitePackageId ?? active.businessGraph.layout?.sitePackageId ?? "package-standard-5";
  const sitePackageId = options?.allowAutoDowngrade
    ? resolveGenerationSitePackageId(active.businessGraph, requestedRaw)
    : (() => {
        const preferred = SitePackageIdSchema.parse(
          SitePackageIdSchema.safeParse(requestedRaw).success
            ? requestedRaw
            : "package-standard-5",
        );
        if (!isGenerationPackageEligible(active.businessGraph, preferred)) {
          const readiness = evaluateBusinessProfileReadiness(active.businessGraph);
          const blockers =
            readiness.hardBlockers.length > 0
              ? readiness.hardBlockers.join("; ")
              : readiness.missingRequirements.join("; ") ||
                "profile not ready for this site package";
          throw new Error(`Business profile cannot generate ${preferred} yet: ${blockers}`);
        }
        return preferred;
      })();
  const strategyId = resolveStrategyIdForGraph(active.businessGraph);
  const businessGraph = {
    ...active.businessGraph,
    layout: {
      ...active.businessGraph.layout,
      sitePackageId,
      lensId: options?.lensId ?? active.businessGraph.layout?.lensId ?? "lens-flat-2",
      strategyId,
    },
  };
  const recipeId = recipeIdFromSitePackageId(sitePackageId);
  const packPin = resolvePackPinFromGraph(businessGraph, recipeId);
  const agencyRequestedSitePackageId = options?.agencyRequestedSitePackageId
    ? SitePackageIdSchema.safeParse(options.agencyRequestedSitePackageId).success
      ? SitePackageIdSchema.parse(options.agencyRequestedSitePackageId)
      : undefined
    : undefined;

  const idempotencyKey = buildGenerateIdempotencyKey(projectId, revisionId);
  const result = await api.createRun(projectId, {
    businessGraph,
    recipeId,
    packPin,
    sitePackageId,
    agencyRequestedSitePackageId,
    idempotencyKey,
  });

  return { runId: result.runId, status: "QUEUED" };
}

export async function loadIntakeSummary(
  api: StudioApiClient,
  projectId: string,
): Promise<StudioIntakeSummaryV1 | null> {
  try {
    return await api.getProjectIntakeSummary(projectId);
  } catch (error) {
    if (error instanceof Error && error.message.includes("404")) {
      return null;
    }
    if (error instanceof Error && error.message.includes("client_intake_not_found")) {
      return null;
    }
    throw error;
  }
}

export async function createDraftFromIntakeAndActivate(
  api: StudioApiClient,
  projectId: string,
  clientLinkId: string,
): Promise<{ readonly draftId: string }> {
  const draft = await api.createDraftFromIntake(projectId, clientLinkId);
  const validation = await api.validateBusinessGraphDraft(projectId, draft.draftId);
  if (!validation.valid) {
    const detail = (validation.issues ?? []).map((i) => i.message).join("; ") || "Validation failed";
    throw new Error(detail);
  }
  await api.activateBusinessGraphDraft(projectId, draft.draftId);
  return { draftId: draft.draftId };
}

export type ConfirmFactsAndBuildPreviewResult =
  | { readonly kind: "generated"; readonly runId: string }
  | { readonly kind: "validation_blocked"; readonly issues: readonly string[] }
  | { readonly kind: "package_eligibility_blocked"; readonly blocked: PackageEligibilityBlocked };

export async function confirmFactsAndBuildPreview(
  api: StudioApiClient,
  projectId: string,
  context: {
    readonly intakeSummary: StudioIntakeSummaryV1 | null;
    readonly latestDraftId: string | null;
    readonly currentBusinessGraphRevisionId: string | null;
    readonly sitePackageId?: string;
    /** Agency preference before accepting a smaller built package (Phase 3). */
    readonly agencyRequestedSitePackageId?: string;
    readonly lensId?: string;
    readonly intakeSubmitted?: boolean;
    readonly clientLinkId?: string | null;
  },
): Promise<ConfirmFactsAndBuildPreviewResult> {
  // First preview build: submitted intake → draft → validate → candidate activate (queues run).
  // Do not require or read an active BusinessGraph head; worker advances active head after QA.
  if (!context.currentBusinessGraphRevisionId) {
    let draftId = context.latestDraftId;
    if (!draftId) {
      const intakeReady =
        context.intakeSubmitted === true || context.intakeSummary?.status === "SUBMITTED";
      if (!intakeReady) {
        throw new Error("Waiting for client intake or an active Business Profile before building preview.");
      }
      const clientLinkId = context.intakeSummary?.clientLinkId ?? context.clientLinkId;
      if (!clientLinkId) {
        throw new Error("Client link is required before building preview from intake.");
      }
      const draft = await api.createDraftFromIntake(projectId, clientLinkId);
      draftId = draft.draftId;
    }

    const validation = await api.validateBusinessGraphDraft(projectId, draftId);
    if (!validation.valid) {
      return {
        kind: "validation_blocked",
        issues: (validation.issues ?? []).map((issue) => issue.message),
      };
    }

    try {
      const activation = await api.activateBusinessGraphDraft(projectId, draftId);
      if (typeof activation.runId === "string" && activation.runId.length > 0) {
        return { kind: "generated", runId: activation.runId };
      }
      if (activation.activatedAt && activation.revisionId) {
        // Idempotent replay after worker already advanced the active head — generate against active.
      } else {
        throw new Error("Confirm facts did not queue a generation run. Try again in a moment.");
      }
    } catch (error) {
      if (isStudioEligibilityBlockedError(error)) {
        return {
          kind: "package_eligibility_blocked",
          blocked: packageEligibilityBlockedFromApiBody(error.body),
        };
      }
      throw error;
    }
  }

  const active = await api.getActiveBusinessGraph(projectId);
  const requestedRaw =
    context.sitePackageId ?? active.businessGraph.layout?.sitePackageId ?? "package-standard-5";
  const requestedPackage = SitePackageIdSchema.parse(
    SitePackageIdSchema.safeParse(requestedRaw).success ? requestedRaw : "package-standard-5",
  );
  const eligibility = checkPackageEligibility(active.businessGraph, requestedPackage);
  if (!eligibility.eligible) {
    return { kind: "package_eligibility_blocked", blocked: eligibility.blocked };
  }

  const result = await generateSite(api, projectId, {
    sitePackageId: requestedPackage,
    agencyRequestedSitePackageId: context.agencyRequestedSitePackageId,
    lensId: context.lensId,
    allowAutoDowngrade: false,
  });
  return { kind: "generated", runId: result.runId };
}
